<template>
  <div class="flex flex-col bg-[#f8fafc] gap-6 overflow-hidden h-full">
    <div class="flex-1 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden border-t-4 border-t-[#0d4169] flex flex-col">
      <div class="px-6 pt-5 pb-1 flex justify-between items-center shrink-0">
        <h2 class="text-base font-bold text-[#0d4169] text-left">Material Inventory</h2>
      </div>

      <form class="px-6 pb-5 pt-2 shrink-0" @submit.prevent>
        <div class="flex flex-wrap items-center gap-x-12 gap-y-4">
          <div class="flex items-center gap-3 min-w-[240px]">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left shrink-0">
              Date:
            </label>
            <input
              v-model="selectedDate"
              type="date"
              @change="fetchMaterialInventory"
              class="flex-1 px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            >
          </div>
          <div class="flex items-center gap-3 min-w-[280px]">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left shrink-0">
              Department:
            </label>
            <select
              v-model="selectedDepartment"
              @change="fetchMaterialInventory"
              class="flex-1 px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            >
              <option value="" disabled>Select Department</option>
              <option v-for="dept in filteredDepartments" :key="dept.id" :value="dept.id">
                {{ dept.name }}
              </option>
            </select>
          </div>
        </div>
      </form>

      <div class="px-6 pb-6 overflow-auto flex-1 custom-scrollbar">
        <div v-if="loading" class="flex justify-center py-10">
          <span class="spinner !border-[#0d4169]"></span>
        </div>

        <table v-else class="w-full daily-transaction-table min-w-max">
          <thead>
            <tr>
              <th class="text-center">No.</th>
              <th class="text-left">Item Code</th>
              <th class="text-left">Category</th>
              <th class="text-left">Item Name</th>

              <th class="text-center">U.O.M</th>
              <th class="text-center">{{ lastCountHeaderLabel }}</th>
              <th class="text-center">Received</th>
              <th class="text-left">Current Balance</th>
              <th class="text-center">New Qty</th>
              <th class="text-center">Difference</th>
              <!-- <th class="w-32 text-center">U.O.M.Production</th> -->
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in rows" :key="row.id || `${row.itemCode}-${index}`" class="hover:bg-slate-50 transition-colors">
              <td class="text-center">{{ index + 1 }}</td>
              <td class="text-slate-600 text-sm">{{ row.itemCode || '-' }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm">{{ row.categoryName || '-' }}</td>
              <td class="font-medium text-left">{{ row.itemName || '-' }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ row.uomStock || '-' }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ displayLastCount(row) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ displayNumber(row.received ?? row.recieved ?? row.receivedQty ?? row.recievedQty ?? 0) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ displayNumber(calcCurrentBalance(row)) }}</td>
              <td class="text-center">
                <input
                  v-model.number="row.newValue"
                  type="number"
                  placeholder="0"
                  :disabled="isNewQtyDisabled(row)"
                  class="w-[64px] mx-auto px-1 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm text-center bg-red-50 focus:bg-white"
                  :class="{ 'opacity-70 cursor-not-allowed bg-slate-100 focus:bg-slate-100 hover:border-transparent': isNewQtyDisabled(row) }"
                >
              </td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ displayNumber(calcDifference(row)) }}</td>
              <!-- <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ row.uomProduction || '-' }}</td> -->
            </tr>
          </tbody>
        </table>

        <div v-if="rows.length > 0 && !loading" class="flex justify-center mt-6">
          <button
            @click="openSaveModal"
            :disabled="!canSave || isSaving"
            class="px-6 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md hover:bg-[#0a3354] transition-all active:scale-95 shadow-md flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            <span v-if="isSaving" class="spinner !border-white"></span>
            {{ isSaving ? 'Saving...' : saveButtonLabel }}
          </button>
        </div>

        <div v-if="rows.length === 0 && !loading" class="flex flex-col items-center py-20 text-slate-400">
          <svg class="w-12 h-12 mb-2 opacity-20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path></svg>
          <p class="text-sm font-bold">No materials found for this department.</p>
        </div>
      </div>
    </div>

    <div v-if="saveModal.open" class="fixed inset-0 z-[200] flex items-start justify-center px-4 py-8 overflow-y-auto bg-black/50 backdrop-blur-sm" @click.self="closeSaveModal">
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-md transform transition-all animate-in fade-in zoom-in duration-300 text-left relative overflow-hidden">
        <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl">
          <div class="text-left">
            <h3 class="text-base font-bold m-0">Confirm Save Material Count</h3>
          </div>
          <button type="button" class="p-1 hover:bg-white/10 rounded-full transition-colors text-white" @click="closeSaveModal" aria-label="Close save modal">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div class="p-5 space-y-4">
          <div class="text-xs font-semibold text-slate-500">Please enter the code below to confirm that you want to save.</div>

          <div class="px-3 py-2 rounded-md border border-slate-200 bg-green-100 text-[#0d4169] text-sm font-bold tracking-[0.25em] text-center">
            {{ saveModal.code }}
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Enter Code</label>
            <input
              v-model.trim="saveModal.input"
              type="text"
              maxlength="4"
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm uppercase"
            >
          </div>
          <div v-if="saveModal.error" class="text-xs text-red-600 font-semibold">{{ saveModal.error }}</div>
        </div>

        <div class="px-5 py-4 flex items-center justify-end gap-2">
          <button type="button" @click="closeSaveModal" class="px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors">Cancel</button>
          <button
            type="button"
            @click="confirmSave"
            :disabled="isSaving"
            class="px-5 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all disabled:opacity-60"
          >
            {{ isSaving ? 'Saving...' : 'Confirm' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { apiFetch } from '../../../utils/api'
import store from '../../../store'

const departments = ref([])
const rows = ref([])
const loading = ref(false)
const selectedDepartment = ref('')
const selectedDate = ref('')
const isSaving = ref(false)
const saveModal = ref({
  open: false,
  code: '',
  input: '',
  error: ''
})

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || currentUser.value?.company?.id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || currentUser.value?.branch?.id || null)

const userDeptList = computed(() => {
  const depts = currentUser.value?.departments || currentUser.value?.department?.name || currentUser.value?.departmentName || currentUser.value?.dept
  if (!depts) return []
  if (typeof depts === 'string') return depts.split(',').map((d) => d.trim()).filter(Boolean)
  if (Array.isArray(depts)) return depts.filter(Boolean)
  return [depts].filter(Boolean)
})

const filteredDepartments = computed(() => {
  if (!userDeptList.value.length) return departments.value
  return departments.value.filter((d) =>
    userDeptList.value.some((userDept) => userDept.toLowerCase() === String(d.name || '').toLowerCase())
  )
})

const hasSavedRows = computed(() => rows.value.some((r) => Number(r?.hasSavedValue || 0) === 1))

const canSave = computed(() => {
  if (isSaving.value) return false
  if (!selectedDepartment.value || !selectedDate.value) return false
  if (rows.value.length === 0) return false

  if (!hasSavedRows.value) return true

  const hasEditPermission = rows.value.some((r) =>
    Number(r?.hasSavedValue || 0) === 1 && canEditNewQtyForRow(r)
  )
  if (!hasEditPermission) return false

  return true
})

const saveButtonLabel = computed(() => (hasSavedRows.value ? 'Update' : 'Save'))

watch(filteredDepartments, (newVal) => {
  if (newVal.length === 1 && !selectedDepartment.value) {
    selectedDepartment.value = newVal[0].id
    fetchMaterialInventory()
  }
}, { deep: true })

async function fetchDepartments() {
  try {
    const query = branchId.value
      ? `branchId=${branchId.value}`
      : (companyId.value ? `companyId=${companyId.value}` : '')
    const url = query ? `/departments?${query}` : '/departments'
    const res = await apiFetch(url)
    if (!res.ok) return
    const data = await res.json().catch(() => [])
    const raw = Array.isArray(data) ? data : []
    departments.value = raw.filter(d => {
      if (Number(d.status ?? 1) !== 1) return false
      const isCentral = d.isCentral ?? d.is_central
      return isCentral === 0 || isCentral === false
    })
    departments.value.sort((a, b) => a.name.localeCompare(b.name))
  } catch (err) {
    console.error('Error fetching departments:', err)
  }
}

async function fetchMaterialInventory() {
  if (!selectedDepartment.value) {
    rows.value = []
    return
  }
  loading.value = true
  try {
    const query = new URLSearchParams({
      departmentId: String(selectedDepartment.value),
      selectedDate: String(selectedDate.value || '')
    })
    const res = await apiFetch(`/material-selection/inventory?${query.toString()}`)
    if (!res.ok) {
      rows.value = []
      return
    }
    const data = await res.json().catch(() => [])
    rows.value = Array.isArray(data)
      ? data.map((row) => ({
        ...row,
        newValue: row?.newValue ?? '',
        _savedNewValue: row?.newValue ?? '',
        _savedDifference: row?.difference
      }))
      : []
  } catch (err) {
    console.error('Error fetching material inventory:', err)
    rows.value = []
  } finally {
    loading.value = false
  }
}

function toNumberSafe(value) {
  const n = Number(value ?? 0)
  return Number.isFinite(n) ? n : 0
}

function displayNumber(value) {
  return toNumberSafe(value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

function formatDateOnly(value) {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 10)
}

function displayLastCount(row) {
  const lastCount = toNumberSafe(row?.previous ?? row?.previousQty ?? row?.prev ?? 0)
  return displayNumber(lastCount)
}

const lastCountHeaderLabel = computed(() => {
  const firstDate = formatDateOnly(rows.value?.[0]?.previousFromSavedDate)
  return firstDate ? `Last Count / ${firstDate}` : 'Last Count / Date'
})

function calcCurrentBalance(row) {
  const savedCurrentBalance = Number(row?.currentBalance)
  if (Number(row?.hasSavedValue || 0) === 1 && Number.isFinite(savedCurrentBalance)) {
    return savedCurrentBalance
  }
  const lastCount = toNumberSafe(row?.previous ?? row?.previousQty ?? row?.prev ?? 0)
  const received = toNumberSafe(row?.received ?? row?.recieved ?? row?.receivedQty ?? row?.recievedQty ?? 0)
  return lastCount + received
}

function calcDifference(row) {
  const savedDifference = Number(row?.difference ?? row?._savedDifference)
  if (Number(row?.hasSavedValue || 0) === 1 && Number.isFinite(savedDifference)) {
    return savedDifference
  }
  const currentBalance = calcCurrentBalance(row)
  const newValue = toNumberSafe(row?.newValue ?? 0)
  return newValue - currentBalance
}

function canEditNewQtyForRow(row) {
  const access = row?.material_edit_access || row?.materialEditAccess || null
  const flag = access?.new_qty ?? access?.newQty
  if (flag != null) return flag === true || flag === 1
  return row?.canEditNewQty === true || row?.canEditNewQty === 1
}

function isNewQtyDisabled(row) {
  if (Number(row?.hasSavedValue || 0) !== 1) return false
  return !canEditNewQtyForRow(row)
}

function generateVerificationCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let out = ''
  for (let i = 0; i < 4; i += 1) {
    out += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return out
}

function openSaveModal() {
  if (!canSave.value) return
  saveModal.value.open = true
  saveModal.value.code = generateVerificationCode()
  saveModal.value.input = ''
  saveModal.value.error = ''
}

function closeSaveModal() {
  if (isSaving.value) return
  saveModal.value.open = false
}

async function confirmSave() {
  const expected = String(saveModal.value.code || '').trim()
  const entered = String(saveModal.value.input || '').trim().toUpperCase()
  if (!entered || entered !== expected) {
    saveModal.value.error = 'Invalid code. Please enter the exact 4-character code.'
    return
  }
  saveModal.value.error = ''
  const ok = await saveAllRows()
  if (ok) {
    saveModal.value.open = false
  }
}

async function saveAllRows() {
  const rowsToSave = rows.value.map((row) => {
    const newQty = toNumberSafe(row?.newValue)
    const lastCount = toNumberSafe(row?.previous ?? row?.previousQty ?? row?.prev ?? 0)
    const received = toNumberSafe(row?.received ?? row?.recieved ?? row?.receivedQty ?? row?.recievedQty ?? 0)
    const isSavedRow = Number(row?.hasSavedValue || 0) === 1
    const currentBalance = isSavedRow
      ? (lastCount + received)
      : calcCurrentBalance(row)
    const difference = isSavedRow
      ? (newQty - currentBalance)
      : calcDifference(row)
    return {
      materialInventoryId: row?.materialInventoryId ?? null,
      itemId: row?.id,
      lastCount,
      received,
      currentBalance,
      newQty,
      difference
    }
  })

  isSaving.value = true
  try {
    const res = await apiFetch('/material-selection/inventory/save', {
      method: 'POST',
      body: JSON.stringify({
        departmentId: selectedDepartment.value,
        selectedDate: selectedDate.value,
        rows: rowsToSave
      })
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      alert(err?.message || 'Failed to save material inventory.')
      return false
    }
    await fetchMaterialInventory()
    alert('Material inventory saved successfully!')
    return true
  } catch (err) {
    console.error('Error saving material inventory:', err)
    alert('Failed to save material inventory.')
    return false
  } finally {
    isSaving.value = false
  }
}

onMounted(() => {
  selectedDate.value = new Date().toISOString().split('T')[0]
  fetchDepartments()
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
  border-collapse: collapse;
}

th, td {
  padding: 6px 8px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

.daily-transaction-table td:nth-child(6),
.daily-transaction-table td:nth-child(7),
.daily-transaction-table td:nth-child(8),
.daily-transaction-table td:nth-child(9),
.daily-transaction-table td:nth-child(10) {
  text-align: center;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 20;
}

.daily-transaction-table {
  width: 100%;
}


.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f5f9;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid #cbd5e1;
  border-top-color: transparent;
  border-radius: 50%;
  animation: material-spinner-rotate 0.8s linear infinite;
  will-change: transform;
}

@keyframes material-spinner-rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
